import {useCallback, useEffect, useRef} from 'react';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {
  SaveReviewVulnerabilities,
  SaveReviewVulnerabilitiesVariables,
} from '../../schema-types/SaveReviewVulnerabilities';
import {ContactVulnerabilityDraftInput} from '../../schema-types/globalTypes';
import validateUUID from '../../utils/validateUUID';
import {SAVE_REVIEW_VULNERABILITIES} from '../../features/vulnerabilities/vulnerabilityQueries';

export const AUTOSAVE_DELAY_MS = 800;

export type SectionPayload = () => ContactVulnerabilityDraftInput;

/**
 * Persists the SFP Vulnerability tube stop's draft state (new rows and
 * "Continuing? = No" flags) against the review, so it survives navigation.
 * Nothing reaches Curo here; `SubmitReview` does that on the backend.
 *
 * Each contact section registers a function that builds its part of the
 * payload; `save` collects them all in one mutation and refetches the review
 * validation so the tube stop turns red or green.
 */
export const useReviewVulnerabilityDrafts = (
  reviewId: string | undefined,
  /** Contact ids in display order; the payload is sent in the same order. */
  contactIds: string[]
) => {
  const sections = useRef<Record<string, SectionPayload>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {enqueueSnackbar} = useSnackbar();
  const [saveMutation, {loading}] = useMutation<
    SaveReviewVulnerabilities,
    SaveReviewVulnerabilitiesVariables
  >(SAVE_REVIEW_VULNERABILITIES, {
    // Operation name of REVIEW_VALIDATION, so the stepper re-validates.
    refetchQueries: ['ReviewDetails'],
  });

  const registerSection = useCallback(
    (contactId: string, getPayload: SectionPayload | null) => {
      if (getPayload) {
        sections.current[contactId] = getPayload;
      } else {
        delete sections.current[contactId];
      }
    },
    []
  );

  const save = useCallback(async (): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!reviewId) {
      return true;
    }
    // Sections re-register on every edit, so key order in `sections` is not
    // stable; the page's contact order is.
    const contacts = contactIds
      .map((contactId) => sections.current[contactId])
      .filter((getPayload): getPayload is SectionPayload => Boolean(getPayload))
      .map((getPayload) => getPayload());
    if (contacts.length === 0) {
      return true;
    }
    try {
      const {data} = await saveMutation({
        variables: {reviewId: validateUUID(reviewId), contacts},
      });
      const result = data?.saveReviewVulnerabilities;
      if (!result?.ok) {
        enqueueSnackbar(
          `Could not save vulnerabilities: ${
            result?.errors?.join(', ') || 'unknown error'
          }`,
          {variant: 'error'}
        );
        return false;
      }
      return true;
    } catch (error) {
      enqueueSnackbar('Could not save vulnerabilities. Please try again.', {
        variant: 'error',
      });
      return false;
    }
  }, [reviewId, contactIds, saveMutation, enqueueSnackbar]);

  /** Debounced save for edits made in the tables. */
  const scheduleSave = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      save();
    }, AUTOSAVE_DELAY_MS);
  }, [save]);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  return {registerSection, save, scheduleSave, saving: loading};
};
